<template>
  <div class="acc-hotel">
    <div class="acc-hotel__top">
      <div class="acc-hotel__left">
        <div class="acc-hotel__id">
          {{ orderNumber }}.
          <router-link
            v-if="!isNameLinkDisable"
            :to="{ name: 'reservation-hotels-general', params: { id: reservation.id } }"
            class="acc-hotel__link"
          >
            Reservation #ATH - {{ reservation.id }}
          </router-link>
          <span v-else>Reservation #ATH - {{ reservation.id }}</span>
        </div>
        <div class="acc-hotel__tower">
          <div class="acc-hotel__name-wrapper">
            <div class="acc-hotel__name">{{ reservation.hotel.name }}</div>
            <span v-if="reservation.amendment_request" :style="{ 'backgroundColor': '#f0c427'  }"
                  class="acc-hotel__status">
              Amendment requested
            </span>
            <span v-if="reservation.cancellation_request" :style="{ 'backgroundColor': '#f36161'  }"
                  class="acc-hotel__status">
              Cancellation requested
            </span>
            <span
              v-if="!reservation.rooming_list && reservation.reservation_status.code === 'booked'"
              :style="{ 'backgroundColor': '#439ad1' }"
              class="acc-hotel__status"
            >
              Missing rooming list
            </span>
            <span v-if="countdown" :style="{ backgroundColor: countdownColor }" class="acc-hotel__status">
              {{ countdownDiff.time_string }}
            </span>
          </div>
          <div class="acc-hotel__timing">
            <div class="acc-hotel__time">
              <div class="acc-hotel__time-text">Period:</div>
              <div class="acc-hotel__time-value">
                {{ dateFormat(reservation.check_in) }} - {{ dateFormat(reservation.check_out) }}
              </div>
            </div>
            <div class="acc-hotel__time">
              <div class="acc-hotel__time-text">Meal plan:</div>
              <div class="acc-hotel__time-value">{{ reservation.meal_plan.map(mp => mp.name).join(', ') }}</div>
            </div>
            <div
              v-if="reservation.reservation_status.code === 'booked_provisionally'"
              class="acc-hotel__time"
            >
              <span
                v-if="reservation.is_deadline_expired"
                class="acc-hotel__status"
                style="background-color: #e85c20"
              >
                Deadline: {{ dateFormat(reservation.reservation_deadline) }}
              </span>
              <template v-else>
                <div class="acc-hotel__time-text">Deadline:</div>
                <div class="acc-hotel__time-value">{{ dateFormat(reservation.reservation_deadline) }}</div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="acc-hotel__button" @click="is_acc_open = !is_acc_open">
      <div class="acc-hotel__status-block">
        <div
          v-if="reservation.lead && reservation.lead.reservation_manager"
          v-tooltip="{ content: reservation.lead.reservation_manager.full_name, placement: 'right', offset: 10 }"
          class="acc-hotel__photo"
        >
          <img :src="getUserAvatar(reservation.lead.reservation_manager.crop.crop_url)" alt="Avatar">
        </div>
        <div v-else class="acc-hotel__photo">
          <img alt="Avatar" src="@/assets/img/temp/avatar-slug.jpg">
        </div>
        <div :style="{ 'backgroundColor': reservation.reservation_status.color || '#000'  }"
             class="acc-hotel__status">
          {{ reservation.reservation_status.name }}
        </div>
      </div>
      <div
        v-tooltip="{ content: reservation.lead ? reservation.lead.manager.full_name : '', placement: 'top-start', offset: 10 }"
        class="acc-hotel__photo"
      >
        <img :src="getUserAvatar(reservation.lead ? reservation.lead.manager.crop.crop_url : '')" alt="Avatar">
      </div>
      <div :style="{ 'backgroundColor': reservation.sales_status.color || '#000'  }" class="acc-hotel__status">
        {{ reservation.sales_status.name }}
      </div>
      <img v-if="reservation.notes" alt="" src="@/assets/img/transfer-notes-icon.svg">
      <div class="acc-hotel__time">
        <span class="acc-hotel__time-text">Client:</span>
        <span class="acc-hotel__name">
        {{ reservation.lead.name ? reservation.lead.name : `Lead #${ reservation.lead?.id }` }}
        </span>
      </div>
      <div :class="{ 'acc-hotel__toggler--open': is_acc_open }" class="acc-hotel__toggler">
        <img alt="" src="@/assets/img/arrow-toggler-bottom-icon.svg">
      </div>
    </div>
    <transition name="slide-block">
      <div v-if="is_acc_open" class="acc-hotel__dropdown">
        <div class="acc-hotel__list">
          <AppHotelCard
            v-for="(room, index) in reservation.rooms_short"
            :key="`hotel_room_${room.hotel_room.id}_${index}`"
            :order_number="index + 1"
            :accomodation_type="room.accomodation_type"
            :check_in="room.check_in"
            :check_out="room.check_out"
            :clearable="false"
            :clients_selected="room.clients"
            :early="room.early_check_in_time"
            :late="room.late_check_out_time"
            :mp="room.meal_plan.name"
            :name="room.hotel_room.name"
            :count="room.count"
          />
        </div>
        <div v-if="reservation.notes" class="acc-hotel__notes">
          <div class="acc-hotel__note-icon">
            <img alt="" src="@/assets/img/note-green-big-icon.svg">
            <span>NOTE (by manager):</span>
          </div>
          <div class="acc-hotel__text">{{ reservation.notes }}</div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { differenceInMinutes, format, parse } from 'date-fns'
import AppHotelCard from '@/components/AppHotelCard.vue'

export default {
  name: 'AppAccordionHotel',
  components: { AppHotelCard },
  props: {
    reservation: {
      type: Object,
      required: true,
    },
    orderNumber: {
      type: [Number, String],
      default: 0
    },
    countdown: {
      type: String,
      default: ''
    },
    isNameLinkDisable: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      is_acc_open: false
    }
  },
  computed: {
    countdownDiff() {
      if (!this.countdown) return null

      return this.calculateTimeDiff(this.countdown)
    },
    countdownColor() {
      if (!this.countdownDiff) return ''

      const diff = this.countdownDiff.difference
      if (diff < 360) return '#77b017'
      if (diff < 1440) return '#f0c427'
      return '#e85c20'
    }
  },
  methods: {
    calculateTimeDiff(start) {
      const difference = differenceInMinutes(new Date(), new Date(start))
      const minutes = difference % 60
      const hours = Math.trunc((difference / 60) % 24)
      const days = Math.trunc(difference / 60 / 24)

      return {
        time_string: `${ days ? `${ days }d ` : '' }${ hours ? `${ hours }h ` : '' }${ minutes }m`,
        difference
      }
    },
    dateFormat(date) {
      if (date) return format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy')
      else return '-'
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    }
  }
}
</script>

<style lang="sass" scoped>
.acc-hotel
  margin-bottom: 10px
  border: 1px solid #dfdfdf
  border-radius: 10px
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08)
  background-color: #fff
  overflow: hidden

.acc-hotel__top
  display: flex
  align-items: center
  justify-content: space-between
  padding: 7px 16px
  border-bottom: 1px solid #e8e8e8

.acc-hotel__left
  display: grid
  grid-template-columns: 240px max-content max-content
  align-items: center
  gap: 20px

.acc-hotel__id
  font-weight: 500

.acc-hotel__photo
  width: 31px
  line-height: 0
  border-radius: 50%
  overflow: hidden

.acc-hotel__link
  display: inline-block
  color: $color-text-base
  font-weight: 500
  transition: color 0.3s

  &:after
    content: ''
    display: block
    height: 2px
    border-radius: 3px
    background-color: #aae182
    transform: translateY(-1px)
    transition: opacity 0.3s

  &:hover
    &:after
      opacity: 0

.acc-hotel__name-wrapper
  display: flex
  gap: 10px
  align-items: center
  margin-bottom: 6px

.acc-hotel__name
  font-weight: 500

.acc-hotel__nights
  color: #5f5f5f

.acc-hotel__button
  position: relative
  display: grid
  grid-template-columns: 240px 31px max-content max-content max-content
  align-items: center
  gap: 20px
  width: 100%
  padding: 10px 16px
  cursor: pointer
  text-align: left

.acc-hotel__status-block
  display: inline-flex
  align-items: center
  gap: 12px

.acc-hotel__status
  display: inline-block
  min-width: 110px
  padding: 4px 8px 5px
  border-radius: 20px
  color: #fff
  font-size: 13px
  font-weight: 600
  text-align: center

.acc-hotel__timing
  display: flex
  align-items: center
  gap: 20px

.acc-hotel__time
  display: flex
  gap: 6px

.acc-hotel__time-text
  color: #5f5f5f

.acc-hotel__time-value
  color: #181818
  font-weight: 500

.acc-hotel__toggler
  position: absolute
  top: 14px
  right: 18px
  transition: transform 0.3s

.acc-hotel__toggler--open
  transform: rotate(180deg)

.acc-hotel__dropdown
  border-top: 1px solid #e8e8e8
  background-color: #fafafa

.acc-hotel__list
  padding: 14px

.acc-hotel__room
  margin-bottom: 10px

  &:last-child
    margin-bottom: 0

.acc-hotel__notes
  display: flex
  align-items: center
  gap: 8px
  padding: 12px 18px 10px
  border-top: 1px solid #e8e8e8

.acc-hotel__note-icon
  flex-shrink: 0
  display: flex
  align-items: center
  gap: 10px
  color: #181818
  font-weight: 500

.acc-hotel__text
  color: #5f5f5f

.acc-hotel__right
  display: flex
  align-items: center
  gap: 10px

// ------------------------ slide-block
.slide-block-enter-active
  transition-duration: 0.3s
  transition-timing-function: ease-in

.slide-block-leave-active
  transition-duration: 0.3s
  transition-timing-function: cubic-bezier(0, 1, 0.5, 1)

.slide-block-enter-to, .slide-block-leave
  max-height: 500px
  overflow: hidden

.slide-block-enter, .slide-block-leave-to
  overflow: hidden
  max-height: 0
</style>